import type { QuestionnaireAnswer } from "@repo/domain";
import type { QuestionnaireItem, QuestionnaireSection } from "../fhir/questionnaire-definition-storage";
import { readQuestionnaireDefinitionRow } from "../fhir/questionnaire-definition-storage";
import { readQuestionnaireInstanceRow } from "../fhir/questionnaire-instance-storage";
import type { QuestionnaireInstanceRow, QuestionnaireRow } from "../fhir/types";
import type { QuestionnaireInstanceDto } from "../views/questionnaire-instance";

type QuestionnaireInstanceItemDto = QuestionnaireInstanceDto["sections"][number]["items"][number];
type QuestionnaireInstanceAnswerDto = Extract<QuestionnaireInstanceItemDto, { kind: "Question" }>["answers"][number];

/**
 * The answered instance read against the definition revision it pinned: content
 * — labels, options, ordering — comes from the definition row, and only what
 * the patient supplied comes from the instance row.
 */
export function projectQuestionnaireInstanceDto(
  resource: QuestionnaireInstanceRow,
  definitionResource: QuestionnaireRow,
): QuestionnaireInstanceDto {
  const instance = readQuestionnaireInstanceRow(resource);
  const definition = readQuestionnaireDefinitionRow(definitionResource);
  const answersByQuestion = new Map<string, QuestionnaireAnswer[]>();
  for (const answer of instance.answers) {
    const questionId = String(answer.questionId);
    answersByQuestion.set(questionId, [...(answersByQuestion.get(questionId) ?? []), answer]);
  }
  const createdAt = instance.assignedAt;
  return {
    instanceId: instance.id,
    patientId: instance.patientId,
    definition: { url: definition.url, version: String(definition.version), name: definition.name },
    status: instance.status,
    scoreDefinitions: definition.scores.map((score) => ({
      scoreId: score.id,
      label: score.label,
      minimum: scorePoints(definition.sections, score.itemIds, Math.min),
      maximum: scorePoints(definition.sections, score.itemIds, Math.max),
      itemIds: score.itemIds,
      ...(score.bands?.length ? { bands: score.bands } : {}),
    })),
    started: instance.answers.length > 0,
    ...(createdAt ? { createdAt } : {}),
    assignedAt: instance.assignedAt,
    ...(instance.completedAt ? { completedAt: instance.completedAt } : {}),
    sections: definition.sections.map((section) => ({
      sectionId: section.id,
      label: section.label,
      ...(section.showTogether ? { showTogether: true } : {}),
      ...enablement(section),
      items: section.items.map((item) => projectItem(item, answersByQuestion)),
    })),
  };
}

/** The lowest or highest sum the score's items can reach, by their option points. */
function scorePoints(
  sections: ReturnType<typeof readQuestionnaireDefinitionRow>["sections"],
  itemIds: readonly string[],
  pick: (...points: number[]) => number,
): number {
  const items = sections.flatMap((section) => section.items);
  return itemIds.reduce((sum, id) => {
    const item = items.find((candidate) => candidate.id === id);
    return (
      sum +
      (item?.kind === "Question" && item.value.kind === "Choice"
        ? pick(...item.value.options.map((option) => option.points ?? 0))
        : 0)
    );
  }, 0);
}

function noOption(options: readonly { id: string; points?: number }[]): string | undefined {
  return options.reduce<(typeof options)[number] | undefined>(
    (lowest, option) => (!lowest || (option.points ?? 0) < (lowest.points ?? 0) ? option : lowest),
    undefined,
  )?.id;
}

/** The pinned revision's conditions, present only when the member has any — an unconditional member is always asked. */
function enablement(member: Pick<QuestionnaireSection, "conditions" | "conditionBehavior">) {
  return {
    ...(member.conditions?.length ? { conditions: member.conditions } : {}),
    ...(member.conditions?.length && member.conditionBehavior === "any" ? { conditionBehavior: "any" as const } : {}),
  };
}

function projectItem(
  item: QuestionnaireItem,
  answersByQuestion: ReadonlyMap<string, readonly QuestionnaireAnswer[]>,
): QuestionnaireInstanceItemDto {
  if (item.kind === "Display") return { kind: "Display", itemId: item.id, text: item.text };
  if (item.kind === "RepeatingGroup") {
    return {
      kind: "RepeatingGroup",
      itemId: item.id,
      label: item.label,
      minOccurrences: item.minOccurrences,
      items: item.items.map((child) => projectItem(child, answersByQuestion)),
    };
  }
  const optionLabels =
    item.value.kind === "Choice"
      ? new Map(item.value.options.map((option) => [option.id, option.label]))
      : new Map<string, string>();
  return {
    kind: "Question",
    ...(item.help === undefined ? {} : { help: item.help }),
    itemId: item.id,
    label: item.label,
    required: item.required,
    ...enablement(item),
    answerType: item.value.kind,
    ...(item.value.kind === "Text" && item.value.format ? { textFormat: item.value.format } : {}),
    ...(item.value.kind === "Integer"
      ? {
          ...(item.value.min === undefined ? {} : { min: item.value.min }),
          ...(item.value.max === undefined ? {} : { max: item.value.max }),
          ...(item.value.step === undefined ? {} : { step: item.value.step }),
        }
      : {}),
    ...(item.value.kind === "Choice"
      ? { choiceCardinality: item.value.cardinality, allowFreeText: item.value.allowFreeText }
      : {}),
    ...(item.askYesNoFirst && item.value.kind === "Choice" ? { noOptionId: noOption(item.value.options) } : {}),
    options:
      item.value.kind === "Choice"
        ? item.value.options.map((option) => ({
            optionId: option.id,
            label: option.label,
            ...(option.detail === undefined ? {} : { detail: option.detail }),
            ...(option.points === undefined ? {} : { points: option.points }),
          }))
        : [],
    answers: (answersByQuestion.get(item.id) ?? []).map(
      (answer): QuestionnaireInstanceAnswerDto => ({
        ...(answer.occurrence === undefined ? {} : { occurrence: answer.occurrence }),
        value:
          answer.value.kind === "Choice"
            ? {
                ...answer.value,
                display:
                  answer.value.freeText ??
                  (answer.value.optionId ? (optionLabels.get(String(answer.value.optionId)) ?? "") : ""),
              }
            : answer.value,
      }),
    ),
  };
}
